// Minimal validator for the subset of JSON Schema the contract uses.
import contract from '../contract/segment-events.json' with { type: 'json' };

type Schema = Record<string, any>;
type Props = Record<string, unknown>;

interface EventRule {
  name: string;
  attribution?: 'direct' | 'touch';
  required_context?: string[];
  group_context?: { required_when: { property: string; values: string[] } };
  required_properties?: string[];
  properties?: Record<string, Schema>;
}

const RULES = new Map<string, EventRule>(
  (contract.events as unknown as EventRule[]).map((e) => [e.name, e]),
);
const DEFINITIONS = contract.definitions as unknown as Record<string, any>;

function propertySchemas(rule: EventRule): Record<string, Schema> {
  const props: Record<string, Schema> = { ...(rule.properties ?? {}) };
  const fields = DEFINITIONS.attribution_fields as Record<string, Schema>;
  for (const [field, schema] of Object.entries(fields)) {
    if (rule.attribution === 'direct') props[field] ??= schema;
    if (rule.attribution === 'touch') {
      props[`first_touch_${field}`] ??= schema;
      props[`last_touch_${field}`] ??= schema;
    }
  }
  return props;
}

function checkValue(path: string, value: unknown, schema: Schema, errors: string[]): void {
  if (schema.$ref) {
    schema = DEFINITIONS[String(schema.$ref).replace('#/definitions/', '')];
  }
  const t = schema.type;
  const ok =
    t === 'string' ? typeof value === 'string'
    : t === 'number' ? typeof value === 'number' && Number.isFinite(value)
    : t === 'integer' ? Number.isInteger(value)
    : t === 'boolean' ? typeof value === 'boolean'
    : t === 'array' ? Array.isArray(value)
    : t === 'object' ? typeof value === 'object' && value !== null && !Array.isArray(value)
    : true;
  if (!ok) {
    errors.push(`${path}: expected ${t}`);
    return;
  }
  if (schema.enum && !schema.enum.includes(value)) errors.push(`${path}: not in enum`);
  if (typeof value === 'string') {
    if (schema.minLength != null && value.length < schema.minLength) errors.push(`${path}: too short`);
    if (schema.maxLength != null && value.length > schema.maxLength) errors.push(`${path}: too long`);
    if (schema.pattern && !new RegExp(schema.pattern).test(value)) errors.push(`${path}: pattern`);
  }
  if (typeof value === 'number') {
    if (schema.minimum != null && value < schema.minimum) errors.push(`${path}: below minimum`);
    if (schema.exclusiveMinimum != null && value <= schema.exclusiveMinimum) errors.push(`${path}: below minimum`);
  }
  if (Array.isArray(value)) {
    if (schema.minItems != null && value.length < schema.minItems) errors.push(`${path}: too few items`);
    if (schema.items) value.forEach((v, i) => checkValue(`${path}[${i}]`, v, schema.items, errors));
  }
  if (t === 'object') {
    const obj = value as Props;
    for (const key of schema.required ?? []) if (obj[key] == null) errors.push(`${path}.${key}: required`);
    for (const [key, v] of Object.entries(obj)) {
      const sub = schema.properties?.[key];
      if (sub) checkValue(`${path}.${key}`, v, sub, errors);
      else if (schema.additionalProperties === false) errors.push(`${path}.${key}: not allowed`);
    }
  }
}

export function validateTrack(event: string, properties: Props, groupId?: string): string[] {
  const rule = RULES.get(event);
  if (!rule) return [`unknown event "${event}"`];
  const errors: string[] = [];
  for (const key of rule.required_properties ?? []) {
    if (properties[key] == null) errors.push(`${key}: required`);
  }
  const needsGroup =
    rule.required_context?.includes('groupId') ||
    (rule.group_context &&
      rule.group_context.required_when.values.includes(
        properties[rule.group_context.required_when.property] as string,
      ));
  if (needsGroup && !groupId) errors.push('context.groupId: required');
  const schemas = propertySchemas(rule);
  for (const [key, value] of Object.entries(properties)) {
    if (value == null) continue;
    const schema = schemas[key];
    if (schema) checkValue(key, value, schema, errors);
  }
  return errors;
}
